import React, { ChangeEvent, useState } from 'react';
import { FormControl, FormHelperText, MenuItem, Select, TextField } from '@material-ui/core';
import { useStyles } from '../personalInformation.styles';
import {
  Errors,
  IPersonalInformationIndividualFields,
  Types,
} from '../../../../shared/interfaces/iform';
import { isFormInputValid } from '../../../../shared/utils/formUtils';
import { FormTypes } from '../../../../shared/interfaces/formTypes.enum';
import { REQUIRED_FIELD_MISSING } from '../../../../shared/utils/textConstants';
import { MAX_ZIPCODE_LENGTH, US_STATES } from '../../../../shared/utils/valueConstants';

const errorsDefault: Errors = {
  firstName: false,
  lastName: false,
  ssn: false,
  dateOfBirth: false,
  phoneNumber: false,
  email: false,
  address: false,
  zipCode: false,
  city: false,
  state: false
};

const types: Types = {
  firstName: FormTypes.Text,
  lastName: FormTypes.Text,
  ssn: FormTypes.SSN,
  dateOfBirth: FormTypes.Date,
  phoneNumber: FormTypes.Phone,
  email: FormTypes.Email,
  address: FormTypes.Address,
  zipCode: FormTypes.ZipCode,
  city: FormTypes.Text,
  state: FormTypes.Text,
};

interface IProps {
  id: string;
  formFields: IPersonalInformationIndividualFields;
  handleSetStepCanAdvance: (id: string, flag: boolean) => void;
  handleValueChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type?: FormTypes,
  ) => void;
  handleFieldChange: (label: string, value: string) => void;
}

const IndividualAccount = (props: IProps) => {
  const { id, formFields, handleSetStepCanAdvance, handleValueChange, handleFieldChange } = props;
  const [errors, setErrors] = useState(errorsDefault);
  const classes = useStyles();

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    const label = e.target.name;
    const value = e.target.value;
    setErrors((prevErrors) => {
      return {
        ...prevErrors,
        [label]: !isFormInputValid(types[label], value),
      };
    });
    validateNextStep();
  };

  const validateNextStep = () => {
    const isFormValid =
      Object.entries(types).filter(
        ([key, value]) => !isFormInputValid(value, formFields[key])
      ).length === 0;
    if (isFormValid) {
      handleSetStepCanAdvance(id, true);
    } else {
      handleSetStepCanAdvance(id, false);
    }
  };

  const onSelect = (e: ChangeEvent<{ value: any, name?: any }>) => {
    handleFieldChange(e.target.name, e.target.value)
  }

  return (
    <FormControl className={classes.root} component="fieldset">
      <TextField
        id="firstName"
        name="firstName"
        label="First Name"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.firstName}
        value={formFields.firstName}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.firstName && REQUIRED_FIELD_MISSING}
      />
      <TextField
        id="lastName"
        name="lastName"
        label="Last Name"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.lastName}
        value={formFields.lastName}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.lastName && REQUIRED_FIELD_MISSING}
      />
      <TextField
        id="ssn"
        name="ssn"
        label="Social Security Number"
        variant="outlined"
        required
        style={{ width: "15rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        inputProps={{ maxLength: 12 }}
        error={errors.ssn}
        value={formFields.ssn}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
      <TextField
        id="dateOfBirth"
        name="dateOfBirth"
        label="Date of Birth"
        placeholder="MM/DD/YYYY"
        variant="outlined"
        required
        style={{ width: "8.688rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.dateOfBirth}
        value={formFields.dateOfBirth}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
      <TextField
        id="phoneNumber"
        name="phoneNumber"
        label="Phone Number"
        variant="outlined"
        style={{ width: "11.75rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        inputProps={{ maxLength: 15 }}
        error={errors.phoneNumber}
        value={formFields.phoneNumber}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        required
      />
      <TextField
        id="email"
        name="email"
        label="Email"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.email}
        value={formFields.email}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />

      <TextField
        id="address"
        name="address"
        label="Address"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        inputProps={{ maxLength: 250 }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.address}
        value={formFields.address}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />

      <TextField
        id="city"
        name="city"
        label="City"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        inputProps={{ maxLength: 250 }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.city}
        value={formFields.city}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.city && REQUIRED_FIELD_MISSING}
      />

      <FormControl error>
        <Select
          id="state"
          name="state"
          variant="outlined"
          required
          style={{ width: "20rem", height: "2.75rem", marginBottom: errors.state ? 0 : "20px" }}
          className={classes.textField}
          error={errors.state}
          value={formFields.state}
          onBlur={handleOnBlur}
          onChange={(e) => onSelect(e)}
          defaultValue=""
          displayEmpty
        >
          <MenuItem value="">
            <span
              className={errors.state ? classes.error : classes.placeholder}
            >
              State *
            </span>
          </MenuItem>
          {US_STATES.map((state) => (
            <MenuItem value={state.code} key={state.code}>
              {state.name}
            </MenuItem>
          ))}
        </Select>
        {errors.state && (
          <FormHelperText>{REQUIRED_FIELD_MISSING}</FormHelperText>
        )}
      </FormControl>

      <TextField
        id="zipCode"
        name="zipCode"
        label="Zip Code"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        inputProps={{ maxLength: MAX_ZIPCODE_LENGTH }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.zipCode}
        value={formFields.zipCode}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
    </FormControl>
  );
};

export default IndividualAccount;
